import { FC } from "react";
import { useFormContext } from "react-hook-form";
import GenericControllerWithInput from "@/components/forms/inputsWithController/GenericController";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import { useTranslation } from "react-i18next";
import DropDown from "@/components/forms/inputs/dropDown";
import { InputWithRef } from "@/components/forms/inputsWithRef/inputWithRef";
import { TextAreaWithRef } from "@/components/forms/inputsWithRef/textAreaWithRef";
import dayjs from "dayjs";
import { FormControlLabel } from "@mui/material";
import styles from "@/components/trackedServiceHeaderBar/trackedServiceHeaderBarForm.module.css";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import GenericDateRange from "@/components/dateTime/genericDateRange";
import { RadioButtonsWithRef } from "@/components/forms/inputsWithRef/radioButtonsWithRef";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";

type CompanySubject = {
  companyId: string;
  isParent: boolean;
  is_active: boolean;
  label: string;
  value: string;
};

interface TrackedServiceHeaderbarFormProps {
  onSubmit?: any;
  trackedServiceTemplateOptions: any[];
  kaerEmployeesOptions: any[];
  resetSignal?: number;
  persistedSubjectSearchValue?: any;
}

const TrackedServiceHeaderbarForm: FC<TrackedServiceHeaderbarFormProps> = ({
  onSubmit,
  trackedServiceTemplateOptions,
  kaerEmployeesOptions,
  resetSignal,
  persistedSubjectSearchValue,
}) => {
  const { t } = useTranslation();
  const { handleSubmit, control, setValue, register, watch } = useFormContext();
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField({
      fieldOrder: [
        "company_subject",
        "tracked_service_template",
        "actual_duration",
        "executed_on",
        "executed_by",
      ],
    });

  const rawCompanySubject = watch("company_subject");

  const isCompanySubject = (value: unknown): value is CompanySubject => {
    return (
      typeof value === "object" &&
      value !== null &&
      "companyId" in value &&
      "value" in value &&
      "label" in value
    );
  };
  const companySubject = isCompanySubject(rawCompanySubject)
    ? rawCompanySubject
    : null;

  return (
    <form
      id="trackedService_headerBar_form"
      noValidate
      onSubmit={handleSubmit(onSubmit, (errors) => {
        focusFirstInvalidField(errors);
      })}
      aria-label={t("trackedservice")}
    >
      <GenericControllerWithInput
        control={control}
        name="company_subject"
        rules={{
          required: {
            value: true,
            message: t("field_required"),
          },
        }}
        renderInput={({ onChange, ref, value }) => (
          <div ref={registerValidationTarget("company_subject")}>
            <FormFieldLabel
              label={t("company")}
              htmlFor="company_subject"
              mandatory
            />
            <DropDownSubjectSearch
              name="company_subject"
              customRef={ref}
              onChange={onChange}
              subdomain={"admin"}
              queryParameter={"companies"}
              useGlobalState={false}
              preExistingValue={value || persistedSubjectSearchValue || null}
            />
          </div>
        )}
      />
      <GenericControllerWithInput
        control={control}
        name="subject"
        renderInput={({ onChange, ref, value }) => (
          <div>
            <FormFieldLabel label={t("person")} htmlFor="subject" />
            <DropDownSubjectSearch
              name="subject"
              customRef={ref}
              onChange={onChange}
              subdomain={"admin"}
              queryParameter={"users"}
              isClearable={true}
              useGlobalState={false}
              preExistingValue={value || null}
              companyIdForFiltering={companySubject?.companyId}
            />
          </div>
        )}
      />

      <GenericControllerWithInput
        control={control}
        name="tracked_service_template"
        rules={{
          required: {
            value: true,
            message: t("field_required"),
          },
        }}
        renderInput={({ onChange, value }) => (
          <div ref={registerValidationTarget("tracked_service_template")}>
            <DropDown
              options={trackedServiceTemplateOptions}
              onChange={onChange}
              name="tracked_service_template"
              label={t("trackedservice_template")}
              defaultValue={value}
              mandatory={true}
              isClearable={true}
              resetSignal={resetSignal}
            />
          </div>
        )}
      />
      <GenericControllerWithInput
        control={control}
        name="actual_duration"
        rules={{
          required: {
            value: true,
            message: t("field_required"),
          },
          valueAsNumber: true,
        }}
        renderInput={({ onChange, ref, value }) => (
          <div ref={registerValidationTarget("actual_duration")}>
            <InputWithRef
              name="actual_duration"
              onChange={onChange}
              label={t("trackedservice_actual_duration")}
              type="number"
              ref={ref}
              value={value ?? ""}
              mandatory={true}
            />
          </div>
        )}
      />
      <GenericControllerWithInput
        control={control}
        name="info_ops"
        renderInput={({ onChange, ref, value }) => (
          <TextAreaWithRef
            label={t("trackedservice_info_ops")}
            name="info_ops"
            onChange={onChange}
            ref={ref}
            value={value}
          />
        )}
      />
      <GenericControllerWithInput
        control={control}
        name="executed_on"
        rules={{
          required: {
            value: true,
            message: t("field_required"),
          },
        }}
        renderInput={({ onChange, value }) => {
          const handleDateChange = (e: any) => {
            if (!e) {
              onChange("");
              return;
            }
            const formattedValue = dayjs
              .utc(dayjs(e).format("YYYY-MM-DD"))
              .startOf("day")
              .toISOString();

            onChange(formattedValue);
          };
          return (
            <div ref={registerValidationTarget("executed_on")}>
              <FormControlLabel
                labelPlacement="top"
                disableTypography
                sx={{
                  display: "flex",
                  margin: "0",
                  alignItems: "start",
                }}
                label={
                  <section>
                    {t("executed_on")}
                    <span style={{ color: "red" }}>*</span>
                  </section>
                }
                control={
                  <GenericDateRange
                    selectionMode="single"
                    className={styles.datePicker}
                    value={value ? dayjs(value) : null}
                    onChange={handleDateChange}
                    onClear={() => {
                      setValue("executed_on", "");
                    }}
                  />
                }
              />
            </div>
          );
        }}
      />
      <GenericControllerWithInput
        control={control}
        name="executed_by"
        rules={{
          required: {
            value: true,
            message: t("field_required"),
          },
        }}
        renderInput={({ onChange, value }) => (
          <div ref={registerValidationTarget("executed_by")}>
            <DropDown
              options={kaerEmployeesOptions}
              onChange={onChange}
              name="executed_by"
              label={t("executed_by")}
              defaultValue={value}
              mandatory={true}
            />
          </div>
        )}
      />
      <GenericControllerWithInput
        control={control}
        name="on_site"
        renderInput={({ onChange, ref, value }) => (
          <RadioButtonsWithRef
            key={value}
            name="on_site"
            label={t("trackedservice_on_site")}
            onChange={onChange}
            ref={ref}
            defaultValue={value}
          />
        )}
      />
      <input type="hidden" {...register("description")} />
      <input type="hidden" {...register("price")} />
      <input type="hidden" {...register("tracked_service_type")} />
      <input type="hidden" {...register("category")} />
      <input type="hidden" {...register("estimated_duration")} />
      <input type="hidden" {...register("shouldAddAnotherEntry")} />
    </form>
  );
};

export default TrackedServiceHeaderbarForm;
